import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

// Bolt numbering from diagrams (position clockwise from top -> bolt number shown)
const BOLT_SEQUENCES = {
    4: [1, 3, 2, 4],
    8: [1, 5, 3, 7, 2, 6, 4, 8],
    12: [1, 5, 9, 3, 7, 11, 2, 6, 10, 4, 8, 12],
    16: [1, 9, 5, 13, 3, 11, 7, 15, 2, 10, 6, 14, 4, 12, 8, 16],
    20: [1, 13, 5, 17, 9, 3, 15, 7, 19, 11, 2, 14, 6, 18, 10, 4, 16, 8, 20, 12],
    24: [4, 20, 12, 8, 16, 24, 1, 17, 9, 5, 13, 21, 3, 19, 11, 7, 15, 23, 2, 18, 10, 6, 14, 22]
};

const BoltPatternDiagram = ({ numBolts, size = 300 }) => {
    const canvasRef = useRef(null);
    const modalCanvasRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [shouldDrawModal, setShouldDrawModal] = useState(0);

    // Function to draw hexagon with proper rotation
    const drawHexagon = useCallback((ctx, x, y, radius, color) => {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            // Rotate by 30 degrees to have flat side on top/bottom
            const angle = (Math.PI / 3) * i + Math.PI / 6;
            const px = x + radius * Math.cos(angle);
            const py = y + radius * Math.sin(angle);
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = '#2a2a2a';
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }, []);

    // Function to draw diagram
    const drawDiagram = useCallback((canvas, canvasSize) => {
        if (!canvas) {
            console.log('Canvas is null!');
            return;
        }

        console.log('drawDiagram called:', { canvasSize, width: canvas.width, height: canvas.height });

        const ctx = canvas.getContext('2d');
        const centerX = canvasSize / 2;
        const centerY = canvasSize / 2;
        const flangeRadius = canvasSize * 0.4;
        const centerHoleRadius = canvasSize * 0.08;

        // Adaptive bolt size based on number of bolts
        let boltRadius;
        if (numBolts <= 8) {
            boltRadius = canvasSize * 0.055;
        } else if (numBolts <= 12) {
            boltRadius = canvasSize * 0.045;
        } else if (numBolts <= 16) {
            boltRadius = canvasSize * 0.038;
        } else if (numBolts <= 20) {
            boltRadius = canvasSize * 0.033;
        } else {
            boltRadius = canvasSize * 0.028;
        }

        const boltCircleRadius = flangeRadius - boltRadius - 15;

        // Clear canvas
        ctx.clearRect(0, 0, canvasSize, canvasSize);

        // Draw flange (gray circle)
        ctx.beginPath();
        ctx.arc(centerX, centerY, flangeRadius, 0, 2 * Math.PI);
        ctx.fillStyle = '#d0d0d0';
        ctx.fill();
        ctx.strokeStyle = '#888';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw center hole
        ctx.beginPath();
        ctx.arc(centerX, centerY, centerHoleRadius, 0, 2 * Math.PI);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.strokeStyle = '#888';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Get bolt numbering sequence for this pattern
        const sequence = BOLT_SEQUENCES[numBolts] || [];

        // Draw bolts - numbering according to sequence
        const angleStep = (2 * Math.PI) / numBolts;
        const startAngle = -Math.PI / 2; // Start from top (12 o'clock position)

        for (let i = 0; i < numBolts; i++) {
            const angle = startAngle + i * angleStep;
            const boltX = centerX + boltCircleRadius * Math.cos(angle);
            const boltY = centerY + boltCircleRadius * Math.sin(angle);

            // Draw hexagonal bolt
            drawHexagon(ctx, boltX, boltY, boltRadius, '#4a4a4a');

            // Draw bolt number from sequence - adaptive font size
            let fontSize;
            if (numBolts <= 8) {
                fontSize = canvasSize * 0.05;
            } else if (numBolts <= 12) {
                fontSize = canvasSize * 0.042;
            } else if (numBolts <= 16) {
                fontSize = canvasSize * 0.036;
            } else if (numBolts <= 20) {
                fontSize = canvasSize * 0.032;
            } else {
                fontSize = canvasSize * 0.028;
            }

            const boltNumber = sequence[i] || (i + 1);

            ctx.fillStyle = '#fff';
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(boltNumber, boltX, boltY);
        }
    }, [numBolts, drawHexagon]);

    // Draw on main canvas
    useEffect(() => {
        drawDiagram(canvasRef.current, size);
    }, [numBolts, size, drawDiagram]);

    // Draw on modal canvas when triggered
    useEffect(() => {
        console.log('Modal draw effect triggered:', { shouldDrawModal, open, hasRef: !!modalCanvasRef.current });
        if (shouldDrawModal > 0 && modalCanvasRef.current) {
            console.log('Actually drawing modal!');
            drawDiagram(modalCanvasRef.current, 600);
        }
    }, [shouldDrawModal, drawDiagram]);

    // Handle modal open
    const handleOpen = () => {
        console.log('Opening modal...');
        setOpen(true);
        // Trigger draw after modal opens
        setTimeout(() => {
            console.log('Triggering modal draw...');
            setShouldDrawModal(prev => prev + 1);
        }, 100);
    };

    return (
        <>
            <Box textAlign="center" p={1}>
                <canvas
                    ref={canvasRef}
                    width={size}
                    height={size}
                    onClick={handleOpen}
                    style={{
                        maxWidth: '100%',
                        height: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        background: '#fafafa',
                        cursor: 'pointer'
                    }}
                />
                <Typography variant="body2" fontWeight="bold" mt={0.5} sx={{ fontSize: '0.85rem' }}>
                    {numBolts}-BOLTS
                </Typography>
            </Box>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                        outline: 'none'
                    }}
                >
                    <IconButton
                        onClick={() => setOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.1)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.2)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box textAlign="center">
                        <canvas
                            ref={modalCanvasRef}
                            width={600}
                            height={600}
                            style={{
                                width: '600px',
                                height: '600px',
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                background: '#fafafa'
                            }}
                        />
                        <Typography variant="h6" fontWeight="bold" mt={2}>
                            {numBolts}-BOLTS
                        </Typography>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default BoltPatternDiagram;
